<template>
  <div class="home-page">
    <div class="home-page__main-screen-container">
      <MainScreen
        :title="pageContent.heroScreen.title"
        :backgroundUri="pageContent.heroScreen.backgroundUri"
        :playersOnline="pageContent.heroScreen.playersOnline"
        :visits="pageContent.heroScreen.visits"
      />
    </div>
    <GamesScreen
      v-if="pageContent.gamesScreen.games.length > 0"
      :title="pageContent.gamesScreen.title"
      :subtitle="pageContent.gamesScreen.subtitle"
      :games="pageContent.gamesScreen.games"
    />
    <AchievementsScreen
      v-if="pageContent.achievementsScreen.stats.length > 0"
      :title="pageContent.achievementsScreen.title"
      :subtitle="pageContent.achievementsScreen.subtitle"
      :backgroundUri="pageContent.achievementsScreen.backgroundUri"
      :stats="pageContent.achievementsScreen.stats"
    />
    <CareersScreen
      v-if="pageContent.careersScreen.jobs.length > 0"
      :title="pageContent.careersScreen.title"
      :subtitle="pageContent.careersScreen.subtitle"
      :description="pageContent.careersScreen.description"
      :jobs="pageContent.careersScreen.jobs"
    />
    <BlogScreen
      v-if="pageContent.newsScreen.news.length > 0"
      class="home-page__blog-screen"
      :title="pageContent.newsScreen.title"
      :subtitle="pageContent.newsScreen.subtitle"
      :news="pageContent.newsScreen.news"
    />
    <BrandsScreen
      v-if="pageContent.brandsScreen.brands.length > 0"
      :title="pageContent.brandsScreen.title"
      :subtitle="pageContent.brandsScreen.subtitle"
      :description="pageContent.brandsScreen.description"
      :brands="pageContent.brandsScreen.brands"
      @button-click="scrollToContactUs"
    />
    <ContactUsScreen
      id="contact-us"
      class="home-page__contact-us-screen"
      :title="pageContent.contactUsScreen.title"
      :subtitle="pageContent.contactUsScreen.subtitle"
      :description="pageContent.contactUsScreen.description"
    />
  </div>
</template>

<script setup>
  import defaultVideoUri from "@/assets/videos/background-4.webm";

  const {
    getCurrentPageSettings,
    getVariables,
    getCompanyGames,
    getAllJobs,
    getAllNews,
  } = useWebsiteContent();

  definePageMeta({
    settings: {
      cover: {
        backgroundUri: defaultVideoUri,
      },
    },
  });

  const { data: pageSettings, suspense: pageSettingsSuspense } =
    getCurrentPageSettings();
  const { data: variables, suspense: variablesSuspense } = getVariables();
  const { data: games, suspense: gamesSuspense } = getCompanyGames();
  const { data: jobs, suspense: jobsSuspense } = getAllJobs();
  const { data: news, suspense: newsSuspense } = getAllNews();

  const pageContent = computed(() => ({
    heroScreen: {
      title: pageSettings.value.cover.title,
      backgroundUri: pageSettings.value.cover.backgroundUri ?? defaultVideoUri,
      playersOnline: variables.value.totalPlayersOnline,
      visits: variables.value.totalVisits,
    },
    gamesScreen: {
      title: pageSettings.value.additional?.gameCardsScreen?.title,
      subtitle: pageSettings.value.additional?.gameCardsScreen?.subtitle,
      games: games.value?.data?.filter((game) => game.isFeatured) ?? [],
    },
    achievementsScreen: {
      backgroundUri:
        pageSettings.value.additional?.achievementsScreen?.backgroundUri,
      title: pageSettings.value.additional?.achievementsScreen?.title,
      subtitle: pageSettings.value.additional?.achievementsScreen?.subtitle,
      stats: [
        {
          title: "monthly visits",
          value: variables.value?.monthlyVisits ?? 0,
          formatDigits: 0,
        },
        {
          title: "games launched",
          value: variables.value?.gamesLaunched ?? 0,
          formatDigits: 0,
        },
        {
          title: "monthly active users",
          value: variables.value?.monthlyActiveUsers ?? 0,
          formatDigits: 0,
        },
        {
          title: "total game visits",
          value: variables.value?.totalVisits ?? 0,
          formatDigits: 1,
        },
      ],
    },
    careersScreen: {
      title: pageSettings.value.additional?.careersScreen?.title,
      subtitle: pageSettings.value.additional?.careersScreen?.subtitle,
      description: pageSettings.value.additional?.careersScreen?.description,
      jobs: jobs.value?.slice(0, 3) ?? [],
    },
    newsScreen: {
      title: pageSettings.value.additional?.newsScreen?.title,
      subtitle: pageSettings.value.additional?.newsScreen?.subtitle,
      news: news.value?.data ?? [],
    },
    brandsScreen: {
      title: pageSettings.value.additional?.brandsScreen?.title,
      subtitle: pageSettings.value.additional?.brandsScreen?.subtitle,
      description: pageSettings.value.additional?.brandsScreen?.description,
      brands: pageSettings.value.additional?.brandsScreen?.brands ?? [],
    },
    contactUsScreen: {
      title: pageSettings.value.additional?.contactUsScreen?.title,
      subtitle: pageSettings.value.additional?.contactUsScreen?.subtitle,
      description: pageSettings.value.additional?.contactUsScreen?.description,
    },
  }));

  await Promise.all([
    pageSettingsSuspense(),
    variablesSuspense(),
    gamesSuspense(),
    jobsSuspense(),
    newsSuspense(),
  ]);

  function scrollToContactUs() {
    const contactUsElement = document.getElementById("contact-us");
    if (contactUsElement) {
      contactUsElement.scrollIntoView({ behavior: "smooth" });
    }
  }
</script>

<style lang="scss">
  .home-page {
    overflow: hidden;

    &__main-screen-container {
      background-color: #eff1f5;
    }

    &__blog-screen {
      background-image: url("@/assets/images/blog-screen-bg.svg");
      background-size: 100% 100%;
      background-position: center bottom;
      background-repeat: no-repeat;
    }

    &__contact-us-screen {
      background: #eff1f5;
    }
  }
</style>
